import { useState, useEffect, useRef } from "react";
import { BadgeCheck, Star, Building2 } from "lucide-react";
import { api } from "@/App";

// Detects a trailing "@token" (0+ chars) in text — suggestions appear as soon as "@" is typed
export const getActiveMentionToken = (text) => {
  const m = /(^|\s)@(\w{0,30})$/.exec(text || "");
  return m ? m[2] : null;
};

// Replaces the trailing @token with the picked @username
export const applyMention = (text, username) =>
  text.replace(/(^|\s)@(\w{0,30})$/, `$1@${username} `);

// Dropdown of eligible mention targets (connections + ADE Pro only, server-filtered)
export function MentionSuggestions({ text, onPick, className = "" }) {
  const [results, setResults] = useState([]);
  const token = getActiveMentionToken(text);
  const timer = useRef(null);

  useEffect(() => {
    if (token === null) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.get(`/mentions/search?q=${encodeURIComponent(token)}`)
        .then((r) => setResults(r.data.results || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [token]);

  if (token === null || results.length === 0) return null;

  return (
    <div className={`absolute left-0 right-0 bottom-full mb-1 z-50 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden max-h-56 overflow-y-auto ${className}`} data-testid="mention-dropdown">
      {results.map((u) => (
        <button
          key={u.user_id}
          onMouseDown={(e) => { e.preventDefault(); onPick(u); }}
          className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
          data-testid={`mention-option-${u.user_id}`}
        >
          <img src={u.picture || "/images/default-avatar.png"} alt="" className="w-8 h-8 rounded-full object-cover bg-gray-100 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex items-center gap-1">
              {u.name}
              {u.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />}
              {u.is_creator && <Star className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
              {u.is_ade_pro && <Building2 className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" />}
            </p>
            {u.username && <p className="text-xs text-gray-400 truncate">@{u.username}</p>}
          </div>
          {u.is_ade_pro && <span className="text-[9px] font-bold uppercase text-cyan-600 bg-cyan-50 dark:bg-cyan-950 rounded-full px-2 py-0.5 flex-shrink-0">ADE Pro</span>}
        </button>
      ))}
    </div>
  );
}
